import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import { SmartImage } from "@/components/SmartImage";
import { formatPrice } from "@/data/menu";
import { useIsDesktop } from "@/hooks/useMediaQuery";
import { motion, useReducedMotion } from "framer-motion";

const NUTRIENTS = [
  { key: "kcal", label: "Valoare energetică", unit: "kcal" },
  { key: "kj", label: "Valoare energetică", unit: "kJ", fromKcal: true },
  { key: "proteine", label: "Proteine", unit: "g" },
  { key: "grasimi", label: "Grăsimi", unit: "g" },
  { key: "saturate", label: "din care acizi grași saturați", unit: "g", sub: true },
  { key: "glucide", label: "Glucide", unit: "g" },
  { key: "zaharuri", label: "din care zaharuri", unit: "g", sub: true },
  { key: "sare", label: "Sare", unit: "g" },
];

// Formatare valoare nutrițională: kJ rotunjit la întreg, restul la 1 zecimală.
const fmtNutrient = (val, unit) => {
  if (val == null) return "—";
  const dec = unit === "kJ" ? 0 : 1;
  const factor = 10 ** dec;
  return String(Math.round(val * factor) / factor);
};

const Block = ({ title, children, variants }) => (
  <motion.section variants={variants} className="border-t border-roty-line pt-4">
    <h4 className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-roty-red">
      {title}
    </h4>
    <div className="mt-2 text-[15px] leading-relaxed text-roty-ink">{children}</div>
  </motion.section>
);

const Soon = () => (
  <p className="text-sm text-roty-ash/80">Se adaugă în curând.</p>
);

// Shared details content (used inside both Dialog and Drawer).
const DetailsBody = ({ product, scroll, TitleComponent }) => {
  const price = formatPrice(product.price);
  const meta = product.weight || product.volume;
  const ingredients = product.ingredients || product.description;
  const hasAllergens = product.allergens && product.allergens.length > 0;
  const hasAdditives = product.additives && product.additives.length > 0;
  const hasTraces = product.traces && product.traces.length > 0;
  const per100g = product.nutritionPer100g;
  const perServing = product.nutritionPerServing;
  const weightGrams = product.weightGrams;
  const hasNutrition = per100g && weightGrams;
  // Valoare per porție: preferă valorile din fișa oficială; altfel calculează din per 100 g.
  const servingValue = (key) => {
    if (perServing && perServing[key] != null) return perServing[key];
    if (per100g && per100g[key] != null) return (per100g[key] * weightGrams) / 100;
    return null;
  };

  const reduce = useReducedMotion();
  const container = reduce
    ? undefined
    : { hidden: {}, show: { transition: { staggerChildren: 0.045, delayChildren: 0.05 } } };
  const item = reduce
    ? { hidden: { opacity: 1 }, show: { opacity: 1 } }
    : {
        hidden: { opacity: 0, y: 8 },
        show: { opacity: 1, y: 0, transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] } },
      };

  return (
    <div className={scroll ? "max-h-[90vh] overflow-y-auto overscroll-contain" : ""}>
      {product.image ? (
        <div className="aspect-[4/3] w-full overflow-hidden bg-roty-cream">
          <SmartImage
            base={product.image}
            alt={product.name}
            width={800}
            height={600}
            widths={[400, 800, 1200]}
            sizes="(max-width: 1023px) 100vw, 560px"
            eager
            className={`h-full w-full ${product.subcategory === "pizza" ? "object-contain" : "object-cover"}`}
          />
        </div>
      ) : null}

      <motion.div variants={container} initial="hidden" animate="show" className="space-y-5 p-5 lg:p-6">
        {/* Titlu + gramaj + preț */}
        <motion.div variants={item} className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <TitleComponent className="roty-display text-3xl leading-none text-roty-red lg:text-4xl">
              {product.name}
            </TitleComponent>
            {meta ? (
              <p className="mt-1.5 text-xs font-bold uppercase tracking-wide text-roty-gold-dark">
                {meta}
              </p>
            ) : null}
          </div>
          {price ? (
            <span className="shrink-0 text-2xl font-extrabold tabular-nums text-roty-red lg:text-3xl">
              {price}
            </span>
          ) : null}
        </motion.div>

        {/* Ingrediente */}
        <Block title="Ingrediente" variants={item}>
          {ingredients ? <p className="text-roty-ash">{ingredients}</p> : <Soon />}
        </Block>

        {/* Aditivi */}
        <Block title="Aditivi" variants={item}>
          {hasAdditives ? (
            <p className="text-roty-ash">{product.additives.join(", ")}</p>
          ) : (
            <Soon />
          )}
        </Block>

        {/* Alergeni */}
        <Block title="Alergeni" variants={item}>
          {hasAllergens ? (
            <div className="flex flex-wrap gap-2">
              {product.allergens.map((a) => (
                <span
                  key={a}
                  className="rounded-full border border-roty-line bg-roty-cream px-3 py-1 text-xs font-semibold text-roty-ink"
                >
                  {a}
                </span>
              ))}
            </div>
          ) : (
            <Soon />
          )}
          {hasTraces ? (
            <p className="mt-2 text-xs text-roty-ash">
              Poate conține urme de: {product.traces.join(", ")}.
            </p>
          ) : null}
        </Block>

        {/* Valori nutriționale 100 g / porție */}
        <Block title="Valori nutriționale" variants={item}>
          {hasNutrition ? (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-roty-ash">
                  <th className="py-2 font-semibold">Nutrient</th>
                  <th className="py-2 text-right font-semibold">/ 100 g</th>
                  <th className="py-2 text-right font-semibold">/ porție ({weightGrams} g)</th>
                </tr>
              </thead>
              <tbody>
                {NUTRIENTS.map((n) => {
                  const base = n.fromKcal
                    ? (per100g?.kcal != null ? per100g.kcal * 4.184 : null)
                    : per100g?.[n.key];
                  const servingKcal = servingValue("kcal");
                  const serving = n.fromKcal
                    ? (servingKcal != null ? servingKcal * 4.184 : null)
                    : servingValue(n.key);
                  return (
                    <tr key={n.key} className="border-t border-roty-line">
                      <td className={n.sub ? "py-2 pl-3 font-normal text-roty-ash" : "py-2 font-semibold text-roty-ink"}>
                        {n.label}
                      </td>
                      <td className="py-2 text-right tabular-nums text-roty-ink">
                        {fmtNutrient(base, n.unit)}
                        <span className="ml-1 text-xs text-roty-ash">{n.unit}</span>
                      </td>
                      <td className="py-2 text-right tabular-nums text-roty-ink">
                        {fmtNutrient(serving, n.unit)}
                        <span className="ml-1 text-xs text-roty-ash">{n.unit}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <Soon />
          )}
        </Block>
      </motion.div>
    </div>
  );
};

// Title element differs (Dialog vs Drawer) for a11y.

export const ProductDetails = ({ product, open, onOpenChange }) => {
  const isDesktop = useIsDesktop();
  if (!product) return null;

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          data-testid="product-dialog"
          className="max-h-[90vh] gap-0 overflow-hidden border-roty-line bg-roty-paper p-0 sm:max-w-[560px] sm:rounded-[8px]"
        >
          <DetailsBody product={product} scroll TitleComponent={DialogTitle} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        data-testid="product-sheet"
        className="max-h-[94vh] border-roty-line bg-roty-paper"
      >
        <DrawerDescription className="sr-only">
          Detalii produs {product.name}
        </DrawerDescription>
        <div className="overflow-y-auto overscroll-contain">
          <DetailsBody product={product} TitleComponent={DrawerTitle} />
        </div>
      </DrawerContent>
    </Drawer>
  );
};
